import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  FiSettings,
  FiPlusCircle,
  FiChevronLeft,
  FiChevronRight,
  FiRefreshCw,
  FiCheck,
  FiX,
  FiCopy,
  FiClipboard
} from 'react-icons/fi';
import { LinearFunctionTable_prompt, Set_linear_table } from '../../gen_graph/Linear_Function_table_1';
import { LinearFunctionGraph_prompt, Set_linear_graph } from '../../gen_graph/Linear_Function_table_2';
import { MacronutrientTable_prompt, Set_macronutrient_table } from '../../gen_graph/Linear_Function_table_3';
import { TwoGraphs_prompt, SetTwoGraphs } from '../../gen_graph/Linear_Function_table_4';

import { BASELINE } from '../../util';

// List of available graph types with their metadata
const graphTypes = [
  {
    id: 'Linear_Function_graph_1',
    title: 'Linear Function Table 1',
    description: 'Linear Function Table showing values of a linear function',
    imageUrl: '/dashboard/Linear_Function_graph_1.png',
    generator: LinearFunctionTable_prompt,
    setter: Set_linear_table
  },
  {
    id: 'Linear_Function_graph2',
    title: 'Linear Function Graph',
    description: 'Graph showing a linear function',
    imageUrl: '/dashboard/Linear_Function_graph_2.png',
    generator: LinearFunctionGraph_prompt,
    setter: Set_linear_graph
  },
  {
    id: 'Linear_Function_graph3',
    title: 'Linear Function table 2',
    description: 'Linear Function Table showing a linear function',
    imageUrl: '/dashboard/Linear_Function_graph_3.png',
    generator: MacronutrientTable_prompt,
    setter: Set_macronutrient_table
  },
  {
    id: 'Linear_Function_twoslope',
    title: 'Linear Function two slope',
    description: 'Linear Function Table showing a linear function',
    imageUrl: '/dashboard/Linear_Function_graph_4.png',
    generator: TwoGraphs_prompt,
    setter: SetTwoGraphs
  }
];


// Sub-topics options
const subTopicOptions = [
  { value: 'linear_equations', label: 'Linear Equations' },
  { value: 'function_analysis', label: 'Function Analysis' },
  { value: 'slope_intercept', label: 'Slope and Y-Intercept' },
  { value: 'function_tables', label: 'Function Tables' },
  { value: 'linear_modeling', label: 'Linear Modeling' }
];

// Difficulty options
const difficultyOptions = [
  { value: 'E', label: 'Easy' },
  { value: 'M', label: 'Medium' },
  { value: 'H', label: 'Hard' }
];

// Question type options
const questionTypeOptions = [
  { value: 'MCQ', label: 'Multiple Choice' },
  { value: 'FRQ', label: 'Free Response' }
];

const MathGraphGeneratorModal = ({ visible, onClose }) => {
  // State variables
  const [selectedGraph, setSelectedGraph] = useState(null);
  const [selectedSubTopic, setSelectedSubTopic] = useState(subTopicOptions[0]);
  const [selectedDifficulty, setSelectedDifficulty] = useState(difficultyOptions[1]);
  const [questionType, setQuestionType] = useState(questionTypeOptions[0]);
  const [generatedGraph, setGeneratedGraph] = useState(null);
  const [generatedGraphData, setGeneratedGraphData] = useState(null);
  const [generatedQuestions, setGeneratedQuestions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState('1');
  const [copySuccess, setCopySuccess] = useState(false);
  const [combinedContent, setCombinedContent] = useState(null);
  const [generatedGraphSVG, setGeneratedGraphSVG] = useState(null);
  const [generatedGraphAltText, setGeneratedGraphAltText] = useState(null);

  // Copy question data to clipboard
  const copyToClipboard = () => {
    if (!generatedQuestions || !generatedGraphData) return;

    try {
      // Get original SVG code
      const svgCode = generatedGraphSVG;
      // Prepare question data based on question type
      const qType = questionType?.value || "MCQ";
      let questionData;

      if (qType === "MCQ") {
        questionData = {
          stem: `${svgCode} ${generatedQuestions.stem}`,
          a: generatedQuestions.a,
          b: generatedQuestions.b,
          c: generatedQuestions.c,
          d: generatedQuestions.d,
          correct_answer: generatedQuestions.correct_answer,
          rationale: generatedQuestions.rationale
        };
      } else {
        questionData = {
          stem: `${svgCode} ${generatedQuestions.stem}`,
          a: "",
          b: "",
          c: "",
          d: "",
          correct_answer: generatedQuestions.correct_answer,
          rationale: generatedQuestions.rationale
        };
      }

      // Create JSON string with proper escaping
      const clipboardText = JSON.stringify(questionData, null, 2);

      // Copy to clipboard
      navigator.clipboard.writeText(clipboardText).then(
        () => {
          setCopySuccess(true);
          setTimeout(() => setCopySuccess(false), 2000);
        },
        (err) => {
          console.error('Could not copy text: ', err);
          setCopySuccess(false);
        }
      );
    } catch (error) {
      console.error('Error copying to clipboard:', error);
    }
  };

  // Generate SAT problems based on the graph
  const generateSATProblems = async (graphData) => {
    if (!graphData) {
      console.error("Cannot generate SAT problems: graphData is undefined");
      setLoading(false);
      return;
    }

    setLoading(true);

    // Track generation attempts
    let attempts = 0;
    const maxAttempts = 5;
    let success = false;

    try {
      const difficultyText = selectedDifficulty?.label || "Medium";
      const subTopicText = selectedSubTopic?.label || "Linear Equations";
      const qType = questionType?.value || "MCQ";

      const prompt = `You are a test author creating ONE SAT-style ${subTopicText} ${qType === "MCQ" ? "multiple-choice" : "free response"} question based on the following graph data:
    
    ${JSON.stringify(graphData, null, 2)}
    
    
    Follow these rules:
    
    1. Produce exactly ONE (1) question that makes use of the provided graph data.
    2. The question must be in JSON format with the following structure and keys:
       {
         "stem": "…",
         ${qType === "MCQ" ? `"a": "…",
         "b": "…",
         "c": "…",
         "d": "…",` : `"a": "",
         "b": "",
         "c": "",
         "d": "",`}
         "correct_answer": ${qType === "MCQ" ? `"X"` : `"answer1", "answer2", ..."`},
         "rationale": "…"
       }
    3. The "stem" should contain a clear, well-worded ${subTopicText} problem that references the graph data and tests student understanding at a ${difficultyText} level.
    4. For ${qType === "MCQ" ? "multiple-choice questions" : "free response questions"}, the answer choices should be as described:
       - If ${qType === "MCQ"}, then these should be four distinct options labeled as A, B, C, and D.
       - If free response, then the "a", "b", "c", and "d" fields should be empty strings.
    5. The "correct_answer" field must be in the format:
        - For MCQ: "A", "B", "C", or "D" and must be the best choice.
        - For free response: a JSON array containing all valid answers, e.g., "answer1", "answer2".
    6. The "rationale" must begin with ${qType === "MCQ" ? `"Choice [X] is the best answer. Because..."` : `"The correct answer is [X]. Because..."`} and must explain the solution process thoroughly.
    7. The question must directly use the data from the graph/table provided. For a linear function table, the question should involve finding the function rule, interpreting values, or working with the slope/intercept of the function.
    8. The question should be at a ${difficultyText} difficulty level.
    
    IMPORTANT: Your response MUST be a valid JSON object and nothing else. No explanations, just a valid JSON.`;

      // Try up to maxAttempts times
      while (attempts < maxAttempts && !success) {
        attempts++;

        try {
          const response = await axios.post(`${BASELINE}askai3`, { prompt });

          if (response.data && response.data.message) {
            try {
              // Parse JSON
              const questionData = JSON.parse(response.data.message);

              // Validate required fields
              if (!questionData.stem ||
                (qType === "MCQ" && (!questionData.a || !questionData.b || !questionData.c || !questionData.d || !questionData.correct_answer)) ||
                !questionData.rationale) {
                console.warn(`Attempt ${attempts}: Generated JSON is missing required fields, retrying...`);
                continue;
              }

              // Successfully parsed, update state
              setGeneratedQuestions(questionData);

              // Create combined component using the setter from the selected graph type
              const combinedComponent = (
                <div className={`${selectedGraph.id === 'Linear_Function_graph_1' ? 'table' : 'graph'}-question-container`}>
                  <div className={`${selectedGraph.id === 'Linear_Function_graph_1' ? 'table' : 'graph'}-container`} style={{ marginBottom: '20px' }}>
                    {selectedGraph.setter({
                      graphData: {
                        ...graphData,
                        options: {
                          showAltText: false,
                          tableWidth: 350,  // For table
                          width: 400,       // For graph
                          height: 300       // For graph
                        }
                      }
                    })}
                  </div>
                  <div className="question-stem" style={{
                    marginTop: '15px',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    lineHeight: '1.5'
                  }}>
                    {questionData.stem}
                  </div>
                </div>
              );

              // Save the combined component
              setCombinedContent(combinedComponent);

              // Set success flag
              success = true;

            } catch (error) {
              console.warn(`Attempt ${attempts}: JSON parsing error - ${error.message}, retrying...`);
            }
          } else {
            console.warn(`Attempt ${attempts}: Unexpected response structure, retrying...`);
          }
        } catch (error) {
          console.warn(`Attempt ${attempts}: API request error - ${error.message}, retrying...`);
        }

        // If failed and more attempts available, wait a bit
        if (!success && attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      // If all attempts failed
      if (!success) {
        console.error(`Failed to generate valid SAT problem after ${maxAttempts} attempts`);
        setGeneratedQuestions(null);
        setCombinedContent(null);
      }

    } catch (error) {
      console.error("Error in generateSATProblems:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle graph selection
  const handleGraphSelect = (graph) => {
    setSelectedGraph(graph);
    setGeneratedGraph(null);
    setGeneratedGraphData(null);
    setGeneratedQuestions(null);
  };

  // handleGenerate 함수 수정
  const handleGenerate = async () => {
    if (!selectedGraph) return;

    setLoading(true);
    try {
      // Generate graph data using AI
      const count = 1; // Generate one graph
      const difficulty = selectedDifficulty.value; // Pass difficulty to the generator
      const result = await selectedGraph.generator(count, difficulty);

      if (result && result[0]) {
        // Store the original data
        setGeneratedGraphData(result[0].data);

        // Set SVG code and alt text
        setGeneratedGraphSVG(result[0].svgCode);
        setGeneratedGraphAltText(result[0].altText);

        // Use the setter from the selected graph type
        const modifiedComponent = selectedGraph.setter({
          graphData: {
            ...result[0].data,
            options: {
              showAltText: false,
              tableWidth: 350, // For table
              width: 400,      // For graph
              height: 300      // For graph
            }
          }
        });

        setGeneratedGraph(modifiedComponent);

        // Generate SAT problems with the data
        await generateSATProblems(result[0].data);
      }
    } catch (error) {
      console.error("Error generating graph:", error);
    } finally {
      setLoading(false);
    }
  };

  // Custom Dropdown Component
  const Dropdown = ({ label, options, value, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
      <div className="relative" style={{ position: 'relative' }}>
        <div
          className="dropdown-header"
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '8px 12px',
            border: '1px solid #d9d9d9',
            borderRadius: '4px',
            cursor: 'pointer',
            backgroundColor: '#fff'
          }}
          onClick={() => setIsOpen(!isOpen)}
        >
          <span>{value?.label || label}</span>
          <FiChevronRight
            style={{
              marginLeft: '8px',
              transform: isOpen ? 'rotate(90deg)' : 'none',
              transition: 'transform 0.3s'
            }}
          />
        </div>

        {isOpen && (
          <div
            className="dropdown-menu"
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              zIndex: 10,
              width: '100%',
              backgroundColor: '#fff',
              border: '1px solid #d9d9d9',
              borderRadius: '4px',
              marginTop: '4px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
            }}
          >
            {options.map(option => (
              <div
                key={option.value}
                className="dropdown-item"
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  backgroundColor: option.value === value?.value ? '#f0f0f0' : 'transparent'
                }}
                onClick={() => {
                  onChange(option);
                  setIsOpen(false);
                }}
              >
                {option.label}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Custom Radio Button Component
  const RadioGroup = ({ options, value, onChange }) => {
    return (
      <div style={{ display: 'flex', gap: '8px' }}>
        {options.map(option => (
          <div
            key={option.value}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '6px 12px',
              border: '1px solid #d9d9d9',
              borderRadius: '4px',
              backgroundColor: option.value === value ? '#1890ff' : '#fff',
              color: option.value === value ? '#fff' : '#000',
              cursor: 'pointer'
            }}
            onClick={() => onChange(option)}
          >
            {option.label}
          </div>
        ))}
      </div>
    );
  };

  // Custom Button Component
  const Button = ({ children, onClick, type = 'default', disabled = false, loading = false, style = {} }) => {
    const baseStyle = {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '8px 16px',
      borderRadius: '4px',
      border: 'none',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      ...style
    };

    const buttonTypes = {
      default: {
        backgroundColor: '#f0f0f0',
        color: '#000'
      },
      primary: {
        backgroundColor: '#1890ff',
        color: '#fff'
      },
      success: {
        backgroundColor: '#52c41a',
        color: '#fff'
      }
    };

    return (
      <button
        style={{ ...baseStyle, ...buttonTypes[type] }}
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
      >
        {loading && <FiRefreshCw className="spin" style={{ marginRight: '8px' }} />}
        {children}
      </button>
    );
  };

  // Custom Card Component
  const Card = ({ title, children, bordered = true, style = {}, titleExtra = null }) => {
    return (
      <div
        style={{
          border: bordered ? '1px solid #d9d9d9' : 'none',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px',
          backgroundColor: '#fff',
          ...style
        }}
      >
        {title && (
          <div style={{
            fontSize: '18px',
            fontWeight: 'bold',
            marginBottom: '16px',
            borderBottom: '1px solid #f0f0f0',
            paddingBottom: '8px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>{title}</div>
            {titleExtra && <div>{titleExtra}</div>}
          </div>
        )}
        {children}
      </div>
    );
  };

  // Modal Overlay Component
  const Modal = ({ children, title, visible, onClose, width = '800px' }) => {
    if (!visible) return null;

    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}
      >
        <div
          style={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            width: width,
            maxWidth: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px',
              borderBottom: '1px solid #f0f0f0'
            }}
          >
            <h2 style={{ margin: 0 }}>{title}</h2>
            <FiX
              style={{ cursor: 'pointer', fontSize: '24px' }}
              onClick={onClose}
            />
          </div>
          <div style={{ padding: '16px' }}>
            {children}
          </div>
        </div>
      </div>
    );
  };

  // Tabs Component
  const Tabs = ({ activeKey, onChange, children }) => {
    const tabs = React.Children.toArray(children);

    return (
      <div>
        <div style={{ display: 'flex', borderBottom: '1px solid #f0f0f0', marginBottom: '16px' }}>
          {tabs.map((tab) => (
            <div
              key={tab.props.tabKey}
              style={{
                padding: '12px 16px',
                cursor: tab.props.disabled ? 'not-allowed' : 'pointer',
                borderBottom: activeKey === tab.props.tabKey ? '2px solid #1890ff' : 'none',
                color: activeKey === tab.props.tabKey ? '#1890ff' : tab.props.disabled ? '#d9d9d9' : '#000',
                opacity: tab.props.disabled ? 0.5 : 1
              }}
              onClick={() => !tab.props.disabled && onChange(tab.props.tabKey)}
            >
              {tab.props.tab}
            </div>
          ))}
        </div>
        {tabs.find(tab => tab.props.tabKey === activeKey)}
      </div>
    );
  };

  // Tab Panel Component
  const TabPanel = ({ tabKey, tab, disabled, children }) => {
    return (
      <div>
        {children}
      </div>
    );
  };

  // Loading Spinner Component
  const Spinner = ({ tip }) => {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <div
          style={{
            display: 'inline-block',
            animation: 'spin 1s linear infinite',
            marginBottom: '8px'
          }}
        >
          <FiRefreshCw size={24} />
        </div>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
        {tip && <div>{tip}</div>}
      </div>
    );
  };

  // Copy button component
  const CopyButton = ({ onClick, success }) => {
    return (
      <div
        onClick={onClick}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '4px 8px',
          backgroundColor: success ? '#52c41a' : '#f0f0f0',
          color: success ? '#fff' : '#000',
          borderRadius: '4px',
          cursor: 'pointer',
          transition: 'all 0.3s'
        }}
      >
        {success ? (
          <>
            <FiCheck style={{ marginRight: '4px' }} />
            Copied!
          </>
        ) : (
          <>
            <FiCopy style={{ marginRight: '4px' }} />
            Copy JSON
          </>
        )}
      </div>
    );
  };

  return (
    <Modal
      title="Math Graph Generator"
      visible={visible}
      onClose={onClose}
      width="1000px"
    >
      <Tabs activeKey={currentTab} onChange={setCurrentTab}>
        <TabPanel tab="Select Graph Type" tabKey="1">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '20px' }}>
            {graphTypes.map(graph => (
              <div
                key={graph.id}
                style={{
                  width: '240px',
                  border: `1px solid ${selectedGraph?.id === graph.id ? '#1890ff' : '#d9d9d9'}`,
                  borderRadius: '8px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'box-shadow 0.3s',
                  boxShadow: selectedGraph?.id === graph.id
                    ? '0 0 0 2px rgba(24, 144, 255, 0.2)'
                    : 'none'
                }}
                onClick={() => handleGraphSelect(graph)}
              >
                <div style={{ height: '160px', padding: '10px' }}>
                  <img
                    alt={graph.description}
                    src={graph.imageUrl}
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                </div>
                <div style={{ padding: '10px' }}>
                  <div style={{ fontWeight: 'bold' }}>{graph.title}</div>
                  <div style={{ color: '#666', fontSize: '14px' }}>{graph.description}</div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ width: '100%', marginBottom: '20px' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '10px' }}>Settings:</div>
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              <div>
                <div style={{ marginBottom: '8px' }}>Sub-Topic:</div>
                <Dropdown
                  label="Select Sub-Topic"
                  options={subTopicOptions}
                  value={selectedSubTopic}
                  onChange={setSelectedSubTopic}
                />
              </div>

              <div>
                <div style={{ marginBottom: '8px' }}>Difficulty:</div>
                <RadioGroup
                  options={difficultyOptions}
                  value={selectedDifficulty.value}
                  onChange={(option) => setSelectedDifficulty(option)}
                />
              </div>

              <div>
                <div style={{ marginBottom: '8px' }}>Question Type:</div>
                <RadioGroup
                  options={questionTypeOptions}
                  value={questionType.value}
                  onChange={(option) => setQuestionType(option)}
                />
              </div>
            </div>
          </div>

          <Button
            type="primary"
            onClick={handleGenerate}
            disabled={!selectedGraph}
            loading={loading}
            style={{ marginTop: '20px' }}
          >
            <FiPlusCircle style={{ marginRight: '8px' }} />
            Generate Graph & Question
          </Button>

          {loading && (
            <Spinner tip="Generating graph and question..." />
          )}

          {generatedGraph && !loading && (
            <div style={{ marginTop: '20px' }}>
              <h3>Generated Graph</h3>
              <div style={{ border: '1px solid #eee', padding: '20px', borderRadius: '8px' }}>
                {generatedGraph}
              </div>
              <Button type="primary" onClick={() => setCurrentTab('2')} style={{ marginTop: '20px' }}>
                <FiChevronRight style={{ marginRight: '8px' }} />
                View Generated Question
              </Button>
            </div>
          )}
        </TabPanel>

        <TabPanel tab="Generated Question" tabKey="2" disabled={!generatedQuestions}>
          {generatedQuestions && (
            <div>
              <Card
                title="SAT Question"
                titleExtra={
                  <CopyButton
                    onClick={copyToClipboard}
                    success={copySuccess}
                  />
                }
              >
                <div style={{ fontWeight: 'bold' }}>{combinedContent}</div>

                {questionType.value === 'MCQ' && (
                  <div style={{ marginTop: '15px' }}>
                    <div>A. {generatedQuestions.a}</div>
                    <div>B. {generatedQuestions.b}</div>
                    <div>C. {generatedQuestions.c}</div>
                    <div>D. {generatedQuestions.d}</div>
                  </div>
                )}
              </Card>

              <Card title="Solution">
                <div>
                  <span style={{ fontWeight: 'bold' }}>Correct Answer: </span>
                  {generatedQuestions.correct_answer.replace(/[\[\]"\\]/g, '')}
                </div>
                <div style={{ marginTop: '10px' }}>{generatedQuestions.rationale}</div>
              </Card>

              <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button onClick={() => setCurrentTab('1')}>
                  <FiChevronLeft style={{ marginRight: '8px' }} />
                  Back to Graph Selection
                </Button>

                <div style={{ display: 'flex', gap: '10px' }}>
                  <Button onClick={copyToClipboard} type={copySuccess ? "success" : "default"}>
                    <FiClipboard style={{ marginRight: '8px' }} />
                    {copySuccess ? 'Copied!' : 'Copy Question JSON'}
                  </Button>

                  <Button type="primary" onClick={handleGenerate}>
                    <FiRefreshCw style={{ marginRight: '8px' }} />
                    Generate Another Question
                  </Button>
                </div>
              </div>
            </div>
          )}
        </TabPanel>
      </Tabs>
    </Modal>
  );
};

export default MathGraphGeneratorModal;